import {RegisterOptions} from "react-hook-form";
import {IntlShape} from "react-intl";

export type ValidationRule = {
    required: boolean;
    minLength?: number;
    maxLength?: number;
    min?: number;
    max?: number;
};

export type Validation<T> = {
    [K in keyof T]: ValidationRule;
};

export type InputValidationRegistration = Pick<RegisterOptions, "required" | "minLength" | "maxLength" | "min" | "max">;

export const getInputValidationProps = (
    rule: ValidationRule,
    intl: IntlShape,
    requiredMessage = "enter-required-field",
): InputValidationRegistration => ({
    required: {
        value: rule.required,
        message: intl.formatMessage({id: requiredMessage}),
    },
    minLength: rule.minLength !== undefined ? {
        value: rule.minLength,
        message: intl.formatMessage({id: "enter-min-characters"}, {min: rule.minLength}),
    } : undefined,
    maxLength: rule.maxLength !== undefined ? {
        value: rule.maxLength,
        message: intl.formatMessage({id: "enter-max-characters"}, {max: rule.maxLength}),
    } : undefined,
    min: rule.min !== undefined ? {
        value: rule.min,
        message: intl.formatMessage({id: "value-must-be-min"}, {min: rule.min}),
    } : undefined,
    max: rule.max !== undefined ? {
        value: rule.max,
        message: intl.formatMessage({id: "value-within-max"}, {max: rule.max}),
    } : undefined,
});

export type InputCurrencyRegistration = Pick<RegisterOptions, "pattern">;

export const getInputCurrencyProps = (intl: IntlShape): InputCurrencyRegistration => ({
    pattern: {
        value: /^\d+([.,]\d{1,2})?$/,
        message: intl.formatMessage({id: "enterValidCurrency"})
    },
});

/**
 * Like `getInputCurrencyProps`, but also negative amounts (e.g. `-0,50`).
 */
export const getInputSignedCurrencyProps = (intl: IntlShape): InputCurrencyRegistration => ({
    pattern: {
        value: /^-?\d+([.,]\d{1,2})?$/,
        message: intl.formatMessage({id: "enterValidCurrency"})
    },
});

/**
 * For optional text fields (`setValueAs`): trims the value and turns an empty
 * value into `null`, as the backend rejects blank strings.
 */
export const emptyToNull = (value: unknown): string | null => {
    if (typeof value !== "string") return null;
    const trimmed = value.trim();
    return trimmed === "" ? null : trimmed;
};